/**
 * Invoice money, computed once for the whole frontend.
 *
 * The invoice editor and the autofattura editor used to add their totals up
 * themselves, each with its own `parseFloat` accumulation. Two float pipelines
 * that round independently drift apart at the cent, and they also drift away
 * from the backend — which means the preview an operator approves and the
 * document that actually leaves can disagree, and SdI answers with a scarto
 * (00423) days later.
 *
 * So this module mirrors `src/fatturapa/xml-generator.js` exactly: every amount
 * is carried as an integer in units of 1e-8 euro, grouped by VAT rate the way
 * DatiRiepilogo groups it, and rounded once — half away from zero — when it
 * becomes a figure someone reads. `BigInt` rather than `Number` because the
 * whole point is that no addition along the way may lose a cent.
 *
 * "Mirrors exactly" is the entire contract, and it is not only about
 * arithmetic. The form submits what it computes here as `totalAmount`, and the
 * backend honours a declared total rather than recomputing it, so every rule
 * about *which* figures go into the sum has to be the same rule on both sides:
 *
 *   - an absent or empty Quantità is one unit, never zero — `lineQuantita()`;
 *   - an absent AliquotaIVA is 22,00% — the `||` chain the creation endpoints
 *     apply, so a blank rate is not silently read as zero-rated;
 *   - the summaries are grouped by (aliquota, natura, esigibilità), which is
 *     how `buildRiepilogo()`, the backend `computeInvoiceTotals()` and the
 *     quadratura validator all group them;
 *   - only the rows that will actually be submitted are counted, which is the
 *     rows carrying a description — see `submittedLineItems()`.
 *
 * Kept free of Svelte imports so it can be unit-tested directly.
 */

const UNIT_DECIMALS = 8;
const UNITS_PER_EURO = 10n ** BigInt(UNIT_DECIMALS);
const UNITS_PER_CENT = UNITS_PER_EURO / 100n;

/** EsigibilitaIVA codes the XSD admits, and the historical default. */
const ESIGIBILITA_CODES = new Set(['I', 'D', 'S']);
const ESIGIBILITA_DEFAULT = 'I';

/**
 * Parse a decimal amount into exact units of 1e-8 euro.
 * Accepts the comma an Italian keyboard produces. Returns `null` when there is
 * nothing usable to read, which every caller treats as "not filled in yet"
 * rather than as zero.
 *
 * @param {string|number|null|undefined} value
 * @returns {bigint|null}
 */
export function parseDecimalUnits(value) {
  if (value == null || value === '') return null;
  const text = typeof value === 'number' ? value.toFixed(UNIT_DECIMALS) : String(value).trim();
  const m = /^([+-]?)(\d*)(?:[.,](\d*))?$/.exec(text);
  if (!m || (!m[2] && !m[3])) return null;
  const frac = (m[3] || '').slice(0, UNIT_DECIMALS).padEnd(UNIT_DECIMALS, '0');
  const magnitude = BigInt((m[2] || '0') + frac);
  return m[1] === '-' ? -magnitude : magnitude;
}

/** Round units to whole cents, half away from zero. */
export function unitsToCents(units) {
  const negative = units < 0n;
  const abs = negative ? -units : units;
  const cents = (abs + UNITS_PER_CENT / 2n) / UNITS_PER_CENT;
  return negative ? -cents : cents;
}

/** Cents as the two-decimal string FatturaPA and the UI both want. */
export function formatCents(cents) {
  const negative = cents < 0n;
  const abs = negative ? -cents : cents;
  return `${negative ? '-' : ''}${abs / 100n}.${String(abs % 100n).padStart(2, '0')}`;
}

/** Two decimals, extended up to eight only when the value needs them. */
function formatUnits(units) {
  const negative = units < 0n;
  const abs = negative ? -units : units;
  let frac = String(abs % UNITS_PER_EURO).padStart(UNIT_DECIMALS, '0').replace(/0+$/, '');
  if (frac.length < 2) frac = frac.padEnd(2, '0');
  return `${negative ? '-' : ''}${abs / UNITS_PER_EURO}.${frac}`;
}

function divRoundHalfUp(numerator, denominator) {
  const negative = (numerator < 0n) !== (denominator < 0n);
  const n = numerator < 0n ? -numerator : numerator;
  const d = denominator < 0n ? -denominator : denominator;
  const q = (n + d / 2n) / d;
  return negative ? -q : q;
}

/** VAT on an already-rounded taxable amount, exactly as the document computes it. */
function impostaCents(imponibileCents, aliquota) {
  const rate = parseDecimalUnits(aliquota);
  if (rate == null) return 0n;
  return divRoundHalfUp(imponibileCents * rate, 100n * UNITS_PER_EURO);
}

/**
 * The quantity a line is billed at, in units — the browser's half of
 * `lineQuantita()`.
 *
 * Absent or empty means one unit, which is what the creation endpoints store
 * and what the document is generated from. Reading it as zero here is how
 * clearing the Quantità cell on a 100,00 € line produced a form that submitted
 * ImportoTotaleDocumento 0.00 against riepiloghi totalling 122,00. An explicit
 * zero is a value and stays zero, and a quantity that cannot be read at all is
 * zero on both sides.
 */
function lineQuantitaUnits(item) {
  const value = item?.quantita;
  if (value == null || value === '') return UNITS_PER_EURO;
  return parseDecimalUnits(value);
}

/** Quantity × unit price, exact, rounded back to units. */
function lineUnits(item) {
  const quantita = lineQuantitaUnits(item);
  if (quantita == null) return 0n;
  const prezzo = parseDecimalUnits(item?.prezzoUnitario) ?? 0n;
  return divRoundHalfUp(quantita * prezzo, UNITS_PER_EURO);
}

/** The rate as the document writes it: always two decimals. */
function normalizeRate(value) {
  const units = parseDecimalUnits(value);
  return units == null ? '0.00' : formatCents(unitsToCents(units));
}

/**
 * The rate a line is taxed at, before formatting.
 *
 * `||` and not `??`: the creation endpoints read a blank rate as the 22,00%
 * default, so reading it here as 0,00% would have the browser compute a
 * zero-rated line the document taxes at 22%.
 */
function lineRate(item) {
  return item?.aliquotaIva || item?.aliquotaIVA || '22.00';
}

/** Uppercased EsigibilitaIVA when it is one the XSD admits, `null` otherwise. */
function normalizeEsigibilita(value) {
  if (value == null || value === '') return null;
  const code = String(value).trim().toUpperCase();
  return ESIGIBILITA_CODES.has(code) ? code : null;
}

/**
 * The rows of the editor that end up on the document.
 *
 * A row the operator has not described yet is dropped from the payload, so it
 * may not be counted in the total either: it would put the declared total above
 * the sum of the riepiloghi with nothing on the document to explain the gap.
 * Both editors build their payload from this, so the lines that are summed and
 * the lines that are sent cannot come apart.
 *
 * @param {Array<object>} lineItems
 * @returns {Array<object>} the same objects, in order, minus the empty rows
 */
export function submittedLineItems(lineItems) {
  return (Array.isArray(lineItems) ? lineItems : []).filter((item) => item?.descrizione);
}

/**
 * Everything a form needs to show, and the exact figures it must submit.
 *
 * The VAT groups are the DatiRiepilogo the document will carry: one per rate,
 * Natura and EsigibilitaIVA combination, with VAT computed on the *rounded*
 * taxable amount, which is what the Agenzia prescribes and what the recipient's
 * accounting system redoes. Two lines at the same rate whose VAT becomes
 * collectable differently are rounded in two separate blocks, and adding them
 * up as one merged block lands a cent away from the document.
 *
 * `lineTotals` and `lineTotalTexts` are indexed by the *input* rows, so the
 * editor can print a per-row total next to every row it is showing; only the
 * submitted rows reach the groups and the totals.
 *
 * @param {Array<object>} lineItems - rows of the editor: quantita, prezzoUnitario, aliquotaIva, natura
 * @param {object} [options]
 * @param {string} [options.esigibilitaIVA] - document-level default, as the form will submit it
 * @returns {{
 *   lineTotals: number[],
 *   groups: Array<{ aliquota: string, natura: string, esigibilita: string,
 *                   imponibile: number, imposta: number,
 *                   imponibileText: string, impostaText: string }>,
 *   imponibile: number, imposta: number, totale: number,
 *   imponibileText: string, impostaText: string, totaleText: string,
 *   impostaScissioneText: string, pagabileText: string,
 *   lineTotalTexts: string[],
 * }} amounts as numbers for display and as exact decimal strings for submission
 */
export function computeInvoiceTotals(lineItems, { esigibilitaIVA } = {}) {
  const items = Array.isArray(lineItems) ? lineItems : [];
  const perLine = items.map(lineUnits);
  const documentEsigibilita = normalizeEsigibilita(esigibilitaIVA) || ESIGIBILITA_DEFAULT;

  const groups = new Map();
  items.forEach((item, i) => {
    if (!item?.descrizione) return;
    const aliquota = normalizeRate(lineRate(item));
    // Natura only ever splits a zero-rated group, exactly as in the document.
    const natura = parseFloat(aliquota) === 0 ? (item?.natura || '') : '';
    const esigibilita = normalizeEsigibilita(item?.esigibilitaIVA) || documentEsigibilita;
    const key = `${aliquota}|${natura}|${esigibilita}`;
    if (!groups.has(key)) groups.set(key, { aliquota, natura, esigibilita, units: 0n });
    groups.get(key).units += perLine[i];
  });

  let imponibileCents = 0n;
  let impostaTotalCents = 0n;
  let impostaScissioneCents = 0n;
  const resolved = [...groups.values()].map((group) => {
    const imponibile = unitsToCents(group.units);
    const imposta = impostaCents(imponibile, group.aliquota);
    imponibileCents += imponibile;
    impostaTotalCents += imposta;
    if (group.esigibilita === 'S') impostaScissioneCents += imposta;
    return {
      aliquota: group.aliquota,
      natura: group.natura,
      esigibilita: group.esigibilita,
      imponibile: centsToNumber(imponibile),
      imposta: centsToNumber(imposta),
      imponibileText: formatCents(imponibile),
      impostaText: formatCents(imposta),
    };
  });

  const totaleCents = imponibileCents + impostaTotalCents;

  return {
    lineTotals: perLine.map((units) => Number(formatUnits(units))),
    lineTotalTexts: perLine.map(formatUnits),
    groups: resolved,
    imponibile: centsToNumber(imponibileCents),
    imposta: centsToNumber(impostaTotalCents),
    totale: centsToNumber(totaleCents),
    imponibileText: formatCents(imponibileCents),
    impostaText: formatCents(impostaTotalCents),
    totaleText: formatCents(totaleCents),
    // What the buyer pays the issuer: under scissione dei pagamenti the
    // administration remits the `S` VAT itself, so it is the total less that
    // VAT — the figure the server defaults a payment instalment to.
    impostaScissioneText: formatCents(impostaScissioneCents),
    pagabileText: formatCents(totaleCents - impostaScissioneCents),
  };
}

/** Cents as a number, for display only — never for arithmetic. */
function centsToNumber(cents) {
  return Number(formatCents(cents));
}
